import { apiFetch } from '@sdc/auth';
import type { KpiPayload, Snapshot, TeamMember } from '../../shared/kpi';

/**
 * API-Client. `apiFetch` aus @sdc/auth hängt das Identity-JWT als Bearer an –
 * dieselbe Session wie im Hub, kein eigener Login.
 *
 * Der Server prüft zusätzlich die App-Rolle `admin`. Ein 403 heisst also nicht
 * „nicht angemeldet", sondern „kein Zugang zu dieser App" – das muss die
 * Oberfläche unterscheiden, sonst schickt sie den User in eine Login-Schleife.
 */
export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
  ) {
    super(message);
  }
}

async function call<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await apiFetch(path, init);
  if (!res.ok) {
    const body = (await res.json().catch(() => null)) as { error?: string } | null;
    throw new ApiError(res.status, body?.error ?? 'Es ist ein Fehler aufgetreten.');
  }
  return (await res.json()) as T;
}

export type SnapshotDto = Omit<Snapshot, 'payload'> & { payload: KpiPayload };

export const api = {
  team: () => call<{ team: TeamMember[]; mock: boolean }>('/api/team'),
  kpis: () => call<{ snapshots: SnapshotDto[]; mock: boolean }>('/api/kpis'),
  history: (userId: string) =>
    call<{ snapshots: SnapshotDto[] }>(`/api/history?user=${encodeURIComponent(userId)}`),
  refresh: () => call<{ date: string; members: number; mock: boolean }>('/api/refresh', { method: 'POST' }),
};

export const euro = (value: number): string =>
  new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(value);

export const dayMonth = (iso: string): string =>
  new Date(`${iso}T12:00:00Z`).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', timeZone: 'UTC' });
